import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Search, Plus, X, CheckSquare, ChevronLeft, ChevronRight } from 'lucide-react';

/*
  Shared chrome for every "*List.js" screen: toolbar (icon/title/refresh,
  search, optional scope badge + selected-count chip + clear-filters) and a
  classic page-based pagination footer. Each screen keeps its own <table>
  markup as children — this only unifies the outer frame (colors, spacing,
  pagination) that was previously hand-duplicated per file.

  Modeled on AssetsList.js's pagination + on-tokens styling, with
  WorkOrderList.js's scope badge and "Selected: N" chip folded in as
  optional, opt-in props.
*/
const ListPageShell = ({
  icon,
  title,
  loading = false,
  onRefresh,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  scopeLabel,
  scopeVariant = 'info', // 'info' (blue) | 'danger' (red) — matches WorkOrderList's Global/Site badge
  selectedCount = 0,
  onClearSelection,
  onClearFilters,
  newButtonLabel,
  onNew,
  canCreate = true,
  toolbarExtra,
  headerBanner,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  onPageChange,
  isRTL = false,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`flex flex-col h-full bg-surface-app overflow-hidden font-bold uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Toolbar */}
      <div className="bg-surface-card p-3 border-b shadow-card z-10 space-y-3">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className={`flex items-center gap-4 flex-wrap ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <h2 className="text-primary font-black italic uppercase text-lg flex items-center gap-2 tracking-tighter">
              {icon}
              {title}
              {onRefresh && <RefreshCw size={20} className={loading ? 'animate-spin text-primary' : 'cursor-pointer text-primary-dark'} onClick={onRefresh} />}
            </h2>

            {scopeLabel && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold ${scopeVariant === 'danger' ? 'bg-status-danger-bg text-status-danger' : 'bg-status-info-bg text-status-info'}`}>
                {scopeLabel}
              </span>
            )}

            {onSearchChange && (
              <div className="relative group">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 text-gray-400 group-focus-within:text-primary transition-colors`} size={14} />
                <input
                  className={`${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 border border-gray-300 w-80 outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft shadow-inner rounded-control font-black text-primary placeholder:font-normal placeholder:italic`}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            )}

            {selectedCount > 0 && (
              <div className={`flex items-center gap-2 bg-status-warning-bg px-3 py-1.5 border border-status-warning-bg rounded-control animate-in fade-in ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CheckSquare size={14} className="text-status-warning font-bold" />
                <span className="text-status-warning font-black uppercase">{t('common.selected', 'Selected')}: {selectedCount}</span>
                {onClearSelection && (
                  <button onClick={onClearSelection} className={`text-gray-400 hover:text-status-danger ${isRTL ? 'mr-2' : 'ml-2'}`}><X size={14} /></button>
                )}
              </div>
            )}

            {onClearFilters && (
              <button onClick={onClearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-status-danger font-bold transition-colors" title={t('common.clear_filters')}>
                <X size={16} strokeWidth={3} /> {t('common.clear_filters', 'CLEAR FILTERS')}
              </button>
            )}
          </div>

          {canCreate && onNew && (
            <button onClick={onNew} className={`bg-primary text-white px-8 py-2.5 rounded-control font-black flex items-center gap-2 uppercase ${isRTL ? 'ml-4' : 'mr-4'} hover:bg-primary-hover transition-all shadow-lg active:scale-95`}>
              <Plus size={18} strokeWidth={3} /> {newButtonLabel}
            </button>
          )}
        </div>

        {toolbarExtra}
      </div>

      {headerBanner}

      {/* Table area (screen-owned) */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border shadow-card rounded-card overflow-hidden">
          {children}
        </div>
      </div>

      {/* Pagination footer */}
      {onPageChange && (
        <div className={`bg-primary-dark text-white p-3 px-10 flex justify-between items-center shadow-2xl z-40 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="text-primary italic">{t('assets.list.db_total', 'Total')}</span>
              <span className="bg-black/20 px-3 py-1 rounded-control shadow-inner border border-white/10 text-white">{totalItems}</span>
            </div>
            <div className={`flex items-center gap-2 border-white/10 ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
              <span className="text-primary italic">{t('assets.list.display_page', 'Page')}</span>
              <span className="text-white">{currentPage}</span>
              <span className="text-white/30 mx-1">/</span>
              <span className="text-white">{totalPages || 1}</span>
            </div>
          </div>

          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="p-2 border border-white/20 rounded-control disabled:opacity-10 hover:bg-accent hover:border-accent transition-all shadow-md active:scale-90"
            >
              <ChevronLeft size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="p-2 border border-white/20 rounded-control disabled:opacity-10 hover:bg-accent hover:border-accent transition-all shadow-md active:scale-90"
            >
              <ChevronRight size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListPageShell;
